import React from 'react';
import { makeStyles, Slider, Icon, Paper, Fab, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { green, red } from '@material-ui/core/colors';
import { FontIcon } from '@moderepo/mode-app-components';
import { MediaState, MediaStatus } from '../../containers/robot-cloud/RCUtils';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            padding                     : theme.spacing(1),
            display                     : 'flex',
            flexDirection               : 'column',
            alignItems                  : 'center',
            justifyContent              : 'center',
            minWidth                    : 100,
            [theme.breakpoints.up('sm')]: {
                padding: theme.spacing(1, 2),
            },
        },

        sliderContainer: {
            width        : '100%',
            display      : 'flex',
            flexDirection: 'column',
            alignItems   : 'center',
        },

        slider: {
            flex: 1,
        },

        markersContainer: {
            width   : '100%',
            height  : 25,
            position: 'relative',
            display : 'inline-block',
        },


        marker: {
            position: 'absolute',
            left    : 0,
            top     : 0,
            cursor  : 'pointer',
            height  : 20,

            '& .marker-pin': {
                borderRadius   : '50%',
                position       : 'absolute',
                backgroundColor: theme.palette.primary.main,

                // Drawer marker
                '&:before': {
                    content     : '""',
                    width       : 20,
                    height      : 20,
                    borderRadius: '50% 50% 50% 0',
                    background  : 'inherit',
                    position    : 'absolute',
                    transform   : 'rotate(-45deg)',
                    left        : '50%',
                    top         : '50%',
                    margin      : '0px 0 0 -10px',
                },
            },

            // Icon
            '& .icon': {
                position : 'absolute',
                fontSize : 12,
                right    : 0,
                top      : '50%',
                left     : 0,
                textAlign: 'center',
                color    : '#ffffff',
                transform: 'translate(-50%, -50%)',
            },
        },


        dateTimeInfo: {
            display       : 'flex',
            flexDirection : 'row',
            justifyContent: 'space-between',
            alignItems    : 'center',
            width         : '100%',
        },

        dateInfo: {
            whiteSpace     : 'nowrap',
            display        : 'flex',
            alignItems     : 'center',
            '&:first-child': {
                alignSelf: 'flex-start',
            },
            '&:last-child': {
                alignSelf: 'flex-end',
            },
        },

        timeInfo: {
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
        },

        timeRangeLabel: {
            fontWeight : 500,
            '&.current': {
                paddingLeft: theme.spacing(1),
            },
            '&.length': {
            },
            '&.divider': {
                padding: theme.spacing(0, 0.5),
            },
        },


        controls: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            position      : 'relative',
            width         : '100%',
            justifyContent: 'space-between',

            '& .left-controls': {
                display       : 'flex',
                alignItems    : 'center',
                justifyContent: 'flex-start',
            },
            '& .right-controls': {
                display       : 'flex',
                alignItems    : 'center',
                justifyContent: 'flex-end',
            },
        },


        controlButton: {
            margin            : theme.spacing(1),
            '&:not(:disabled)': {
                color   : theme.palette.common.white,
                '&.play': {
                    backgroundColor: green[500],
                    '&:hover'      : {
                        backgroundColor: green[600],
                    },
                },
                '&.pause': {
                    backgroundColor: red[500],
                    '&:hover'      : {
                        backgroundColor: red[600],
                    },
                },
                '&.skip': {
                    backgroundColor: green[500],
                    '&:hover'      : {
                        backgroundColor: green[600],
                    },
                },
            },
        },

        controlButtonIcon: {
        },

        settings: {
            position: 'absolute',
            right   : 0,
        },
    };
}, { name: 'MediaPlayerControlBar' });


export interface Marker {
    readonly id: string;
    readonly position: number;
    readonly icon: string;
    readonly color?: string;
}

export interface MediaPlayerControlBarProps {
    readonly className?: string;
    readonly labels: {
        readonly startDate: string;
        readonly endDate: string;
        readonly currentDate: string;
        readonly currentValue: string;
        readonly length: string;
    }
    readonly mediaState: MediaState;
    readonly markers?: readonly Marker[];

    readonly onCurrentTimeChange: (value: number)=> void;
    readonly onRewindClicked: ()=> void;
    readonly onForwardClicked: ()=> void;
    readonly onPlayClicked: ()=> void;
    readonly onPauseClicked: ()=> void;
}


/**
 * This is a generic Control bar used for displaying current status of some media e.g. Video, Robot positions through some time interval.
 * This component is a DUMB component. All it does is displaying the current state based on the values from props. The user of this component
 * must handle all the playing logic.
 * @param props
 */
export const MediaPlayerControlBar = (props: MediaPlayerControlBarProps) => {

    const handleChange = (event: any, newValue: number | number[]) => {
        props.onCurrentTimeChange(newValue as number);
    };

    const classes = useStyle();


    return (
        <Paper className={clsx(classes.root, props.className)}>
            <div className={classes.sliderContainer}>
                {props.markers && props.markers.length > 0 && (
                    <div className={classes.markersContainer}>
                        {props.markers.map((marker: Marker) => {
                            // calculate the position for the marker in % relative to the media's length
                            const left = (marker.position / props.mediaState.length) * 100;
                            return (
                                <div
                                    className={classes.marker}
                                    key={marker.id}
                                    style={{ left: `${left}%` }}
                                    onClick={(event: React.MouseEvent) => {
                                        handleChange(event, marker.position);
                                    }}
                                >
                                    <div
                                        className="marker-pin"
                                        style={{ background: marker.color || '' }}
                                    /><FontIcon className="icon" iconName={marker.icon} />
                                </div>
                            );
                        })}
                    </div>
                )}
                <Slider
                    classes={{
                        root: classes.slider,
                    }}
                    min={0}
                    max={props.mediaState.length}
                    value={props.mediaState.current}
                    onChange={handleChange}
                />
            </div>
            <div className={classes.dateTimeInfo}>
                <div className={classes.dateInfo}>
                    {props.labels.currentDate}
                </div>
                <div className={classes.dateInfo}>
                    <div className={clsx(classes.timeRangeLabel, 'current')}>{props.labels.currentValue}</div>
                    <div className={clsx(classes.timeRangeLabel, 'divider')}>/</div>
                    <div className={clsx(classes.timeRangeLabel, 'length')}>{props.labels.length}</div>
                </div>
            </div>

            <div className={classes.controls}>
                <div className="left-controls" />

                <div className="center-controls">
                    <Fab
                        className={clsx(classes.controlButton, 'skip', 'rewind')}
                        color="primary"
                        size="small"
                        disabled={
                            props.mediaState.status !== MediaStatus.PLAYING && props.mediaState.status !== MediaStatus.PAUSE
                        }
                        onClick={() => {
                            props.onRewindClicked();
                        }}
                    >
                        <Icon className={classes.controlButtonIcon}>fast_rewind</Icon>
                    </Fab>

                    {props.mediaState.status !== MediaStatus.PLAYING && (
                        <Fab
                            className={clsx(classes.controlButton, 'play')}
                            color="primary"
                            size="large"
                            onClick={() => {
                                props.onPlayClicked();
                            }}
                        >
                            <Icon className={classes.controlButtonIcon}>play_arrow</Icon>
                        </Fab>
                    )}

                    {props.mediaState.status === MediaStatus.PLAYING && (
                        <Fab
                            className={clsx(classes.controlButton, 'pause')}
                            color="primary"
                            size="large"
                            onClick={() => {
                                props.onPauseClicked();
                            }}
                        >
                            <Icon className={classes.controlButtonIcon}>pause</Icon>
                        </Fab>
                    )}

                    <Fab
                        className={clsx(classes.controlButton, 'skip', 'forward')}
                        color="primary"
                        size="small"
                        onClick={() => {
                            props.onForwardClicked();
                        }}
                        disabled={
                            props.mediaState.status !== MediaStatus.PLAYING && props.mediaState.status !== MediaStatus.PAUSE
                        }
                    >
                        <Icon className={classes.controlButtonIcon}>fast_forward</Icon>
                    </Fab>
                </div>

                <div className="right-controls" />
            </div>
        </Paper>
    );
};
